import { inject, Injectable } from '@angular/core'
import { HttpClient, HttpContext } from '@angular/common/http'
import { map, Observable } from 'rxjs'
import { Product, ProductionTechnology, OfferEntity } from '@onedv-portal/shared'
import { environment } from '../../../environments/environment'
import { GenerateContractNumberRequest, PatchOfferRequest } from '@onedv-portal/api'
import { SPINNER_ENABLED, SPINNER_MESSAGE, SPINNER_TITLE } from '../../shared/interceptors/loading-spinner.interceptor'
import { AuthService } from '../../shared/auth/auth.service'

@Injectable({
  providedIn: 'root',
})
export class OfferService {
  private readonly http = inject(HttpClient)
  private readonly baseUrl = `${environment.apiUrl}/offers/incomplete`
  private readonly authService = inject(AuthService)

  createOffer(
    productionTechnology: ProductionTechnology,
    product: Product,
    companyId?: string
  ): Observable<OfferEntity> {
    if (this.authService.canCreateOffer()) {
      console.log('Creating offer with production technology:', productionTechnology)
      return this.http.post<OfferEntity>(
        this.baseUrl,
        {
          productionTechnology: productionTechnology,
          product: product,
          companyId,
        } satisfies GenerateContractNumberRequest,
        { context: new HttpContext().set(SPINNER_ENABLED, true).set(SPINNER_TITLE, 'Neues Angebot wird erstellt...') }
      )
    }
    throw new Error('Offer creation forbidden.')
  }

  // the section is whatever the product on screen calls it
  patchOffer(contractNumber: string, section: string, data: Record<string, unknown>): Observable<unknown> {
    if (this.authService.canCreateOffer()) {
      console.log(`Patching offer ${contractNumber}`)
      return this.http.patch(this.baseUrl, {
        contractNumber,
        section,
        data,
      } satisfies PatchOfferRequest)
    }
    throw new Error('Offer patching forbidden.')
  }

  getOffer(contractNumber: string): Observable<OfferEntity> {
    if (this.authService.canReadOffer()) {
      console.log(`Fetching offer ${contractNumber}`)
      return this.http.get<OfferEntity>(`${this.baseUrl}/${contractNumber}`)
    }
    throw new Error('Offer fetching forbidden.')
  }

  duplicateOffer(contractNumber: string): Observable<OfferEntity> {
    if (this.authService.canCreateOffer()) {
      console.log(`Duplicating offer ${contractNumber}`)
      return this.http.post<OfferEntity>(`${this.baseUrl}/${contractNumber}/duplicate`, {})
    }
    throw new Error('Offer duplication forbidden.')
  }

  deleteOffer(contractNumber: string): Observable<void> {
    if (this.authService.canDeleteOffer()) {
      return this.http.delete(`${this.baseUrl}/${contractNumber}`, { responseType: 'text' }).pipe(map(() => undefined))
    }
    throw new Error('Offer deletion forbidden.')
  }

  createOfferDocument(contractNumber: string, spinnerTitle: string): Observable<OfferEntity> {
    if (this.authService.canCreateOffer()) {
      console.log(`Creating offer document for ${contractNumber}`)
      return this.http.post<OfferEntity>(
        `${this.baseUrl}/${contractNumber}/create-document`,
        {},
        {
          context: new HttpContext().set(SPINNER_ENABLED, true).set(SPINNER_TITLE, spinnerTitle),
        }
      )
    }
    throw new Error('Offer document creation forbidden.')
  }

  rejectOffer(contractNumber: string): Observable<OfferEntity> {
    if (this.authService.canCreateOffer()) {
      console.log(`Rejecting offer ${contractNumber}`)
      return this.http.post<OfferEntity>(`${this.baseUrl}/${contractNumber}/reject`, {})
    }
    throw new Error('Offer rejection forbidden.')
  }

  finalizeOffer(contractNumber: string): Observable<unknown> {
    if (this.authService.canCreateOffer()) {
      console.log(`Finalizing offer ${contractNumber}`)
      return this.http.post(`${this.baseUrl}/${contractNumber}/finalize`, null, {
        context: new HttpContext()
          .set(SPINNER_ENABLED, true)
          .set(
            SPINNER_MESSAGE,
            'Bitte aktualisieren oder schließen Sie das Browserfenster nicht, um eine reibungslose Übermittlung zu gewährleisten.'
          )
          .set(SPINNER_TITLE, 'Vertrag wird übertragen'),
      })
    }
    throw new Error('Offer finalization forbidden.')
  }
}
